import React, { useState, useEffect } from 'react';
import { patch,patchMobile, logo_new, skincareCouple1, login_logo } from '../../assets';
import { Box, Typography, Button as MuiButton, styled, Link, CircularProgress, useMediaQuery, useTheme, Button } from '@mui/material';
import { useAppContext } from '../../context/AppContext';
import LanguageSelector from '../../components/LanguageSelector'
import { createSession } from '../../components/SessionProvider';
import { COMMON_MUI_STYLES } from '../../styles';
// import ErrorIcon from '@mui/icons-material/ErrorOutline';

/** Tamil / Malayalam scripts need a slightly smaller headline on md+ */
function isTamilOrMalayalam(languageCode: string | undefined): boolean {
  if (!languageCode) return false;
  const base = languageCode.toLowerCase().split('-')[0];
  return base === 'ta' || base === 'ml';
}

// Custom styled MUI components
const StyledButton = styled(MuiButton)(() => ({
  borderRadius: '0.3125rem',
  background: 'var(--color-primary)',
  color: 'var(--color-text-inverse)',
  width: 'auto',
  textTransform:'capitalize',
  transition: 'all 0.2s ease-in-out',
  '&:hover': {
    background: '#333333',
    color: "var(--color-text-inverse)",
    transform: 'translateY(-1px)',
    boxShadow: '0 4px 8px rgba(0,0,0,0.2)',
  },
  '&:active': {
    transform: 'translateY(0)',
    boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
  },
  '&.Mui-disabled': {
    color: 'var(--color-text-inverse)',
    background: '#666666',
    cursor: 'not-allowed',
    transform: 'none',
    boxShadow: 'none',
  },
}));


// Shared props interface for mobile and desktop components
interface LandingPageViewProps {
  isCreatingLoginSession: boolean;
  isCreatingSignupSession: boolean;
  selectedLanguage: string;
  showFullDisclaimer: boolean;
  setShowFullDisclaimer: (show: boolean) => void;
  handleHLSkinTermsClick: (e: React.MouseEvent) => void;
  handleOrboPrivacyClick: (e: React.MouseEvent) => void;
  handleHerbalifePrivacyClick: (e: React.MouseEvent) => void;
  handleDistributorPrivacyClick: (e: React.MouseEvent) => void;
  handleLanguageChange: (sessionLanguage: string) => void;
  handleTryNowClick: () => Promise<void>;
  handleLoginClick: () => Promise<void>;
  getTranslation: (key: string, defaultValue: string) => string;
}

// Mobile View Component
const MobileView: React.FC<LandingPageViewProps> = ({
  isCreatingLoginSession,
  isCreatingSignupSession,
  showFullDisclaimer,
  setShowFullDisclaimer,
  handleHLSkinTermsClick,
  handleOrboPrivacyClick,
  handleHerbalifePrivacyClick,
  handleDistributorPrivacyClick,
  handleLanguageChange,
  handleTryNowClick,
  handleLoginClick,
  getTranslation,
}) => {
  return (
    <Box sx={{ 
      width: '100%', 
      height: '100dvh',
      display: 'flex',
      flexDirection: 'column',
      overflow: 'hidden',
    }}>
      {/* Header with Login Button and Language Selector */}
      <Box sx={{ 
        display: 'flex',
        flexDirection: 'row',
        justifyContent: 'space-between',
        alignItems: 'center',
        padding: '8px',
        width: '100%',
      }}>
        <StyledButton
          onClick={handleLoginClick}
          disabled={isCreatingLoginSession}
          sx={{
                lineHeight: "normal",
                fontWeight: 700,
                borderRadius: '24px',
                fontSize: '0.875rem',
              padding: '8px 16px',
              display: 'flex',
              flexDirection: 'row',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '8px',
          }}
        >
          <Box
            component="img"
            src={login_logo}
            alt="Login Logo"
            sx={{
              height: '16px',
              width: 'auto',
              objectFit: 'contain',
              display: 'inline-block',
              verticalAlign: 'middle',
            }}
          />
          <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', marginTop: '4px' }}>
            {isCreatingLoginSession ? <CircularProgress size={20} color="inherit" /> : getTranslation('Txt10', 'Login')}
          </Box>
        </StyledButton>

        <LanguageSelector onLanguageChange={handleLanguageChange} />
      </Box>

      {/* Main Content - Logo and Image in Columns */}
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'flex-start',
          width: '100%',
          flex: 1,
          minHeight: 0,
        }}
      >
        {/* Logo Section */}
        <Box
          sx={{
            width: '80%',
            height: '25%',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: 'center',
            padding: '1rem 0',
            gap: '1.5rem',
            textAlign: 'center',
            flexShrink: 0,
          }}
        >
          <Box
            component="img"
            src={logo_new}
            alt="Company Logo"
            sx={{ 
              height: "100%",
              width: "100%",
              objectFit: "contain",
            }}
          />
        </Box>

        {/* Image Section */}
        <Box
          sx={{
            width: '100%',
            height: '85%',
            flex: '1 1 auto',
            minHeight: 0,
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'flex-end',
            alignItems: 'center',
            marginBottom: '-100px',
          }}
        >
          <Box
            component="img"
            src={skincareCouple1}
            alt="Home illustration"
            sx={{
              width: 'auto',
              maxWidth: '100%',
              height: '95%',
              objectFit: 'cover',
              objectPosition: 'bottom',
            }}
          />
        </Box>
      </Box>

      {/* Bottom Section with Background */}
      <Box
        sx={{
          width: "100%",
          backgroundImage: `url(${patchMobile})`,
          backgroundRepeat: "no-repeat",
          backgroundSize: "100% 100%",
          backgroundPosition: "top center",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "flex-end",
          gap: "0.5rem",
        }}
      >
        <Box sx={{
          width: "100%",
          height: "100%",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "flex-start",
          gap: "0.5rem",
          padding: "100px 0px 20px",
        }}>
          {/* Title */}
          <Box sx={{ 
            width: "90%",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            textAlign: "center",
          }}>
            <Typography
              variant="body1"
              sx={{
                fontSize: '1.2rem',
                lineHeight: 'normal',
                color: 'var(--color-text-primary)',
                fontWeight: 700,
                fontFamily: 'HerbalifeFont',
                textAlign: 'center',
                width: '100%',
              }}
              component="div"
            >
              {getTranslation('Txt3', 'Get a Skin Scan in, Just 60 Seconds')}
            </Typography>
          </Box>

          {/* Disclaimer */}
          <Box sx={{ 
            width: '90%',
            textAlign: 'center',
            mb: 1,
            minHeight: 'fit-content',
          }}>
            <Typography
              sx={{
                fontSize: '0.5rem',
                color: 'rgba(0, 0, 0, 1)',
                fontFamily: 'HerbalifeFont',
                fontWeight: 100,
                lineHeight: 1.3,
              }}
            >
              {getTranslation('Txt813', 'This tool is intended solely for cosmetic awareness purposes and is not a substitute for consultation with a qualified healthcare professional.')}{' '}
              {!showFullDisclaimer && (
                <Link
                  href="#"
                  onClick={(e) => {
                    e.preventDefault();
                    setShowFullDisclaimer(true);
                  }}
                  sx={{
                    fontSize: '0.5rem',
                    fontWeight: 700,
                    color: 'rgba(0, 0, 0, 1)',
                    textDecoration: 'underline',
                    textDecorationColor: '#000',
                    cursor: 'pointer',
                    '&:hover': {
                      color: 'var(--color-text-primary)',
                      textDecorationColor: '#000',
                    },
                  }}
                >
                  {getTranslation('Txt814', 'Read more')}
                </Link>
              )}
              {showFullDisclaimer && (
                <>
                  {getTranslation('Txt815', 'This tool is designed to help users identify a suitable skincare routine based on general information. It does not provide a professional skin assessment, and the results should not be interpreted as diagnosis, prevention, monitoring, prediction, prognosis, treatment, or alleviation of any medical condition or disease.  If you have any concerns about your skin or overall health, please seek advice from a qualified healthcare professional.')}{' '}
                  <br />
                  <br />
                  {'If you wish to delete your personal information from our records, please email us at '}
                  <Box component="span" sx={{ fontWeight: 700 }}>INorderline@herbalife.com</Box>
                  {' with the subject line '}
                  <Box component="span" sx={{ fontWeight: 700 }}>“Personal Data Deletion Request.”</Box>
                  {' We will process your request in accordance with applicable data protection laws.'}
                  <Link
                    href="#"
                    onClick={(e) => {
                      e.preventDefault();
                      setShowFullDisclaimer(false);
                    }}
                    sx={{
                      fontSize: '0.5rem',
                      color: 'rgba(0, 0, 0, 1)',
                      fontWeight: 700,
                      textDecoration: 'underline',
                      textDecorationColor: '#000',
                      cursor: 'pointer',
                      '&:hover': {
                        color: 'var(--color-text-primary)',
                        textDecorationColor: '#000',
                      },
                    }}
                  >
                    {getTranslation('Txt816', 'Show less')}
                  </Link>
                </>
              )}
            </Typography>
          </Box>

          {/* Privacy Policy Links */}
          <Box sx={{ 
            width: '90%',
            textAlign: 'center',
            mb: 0,
            minHeight: 'fit-content',
          }}>
            <Typography
              sx={{
                fontSize: '0.6rem',
                color: 'var(--color-text-primary)',
                fontFamily: 'HerbalifeFont',
                lineHeight: 1.4,
              }}
            >
              {getTranslation("Txt817", "By clicking on Try Now, I agree to the")}{' '}
              <Link
                href="#"
                onClick={handleHLSkinTermsClick}
                sx={{
                  fontSize: '0.6rem',
                  fontWeight: 700,
                  color: 'var(--color-text-primary)',
                  textDecoration: 'underline',
                  textDecorationColor: '#000',
                  cursor: 'pointer',
                  '&:hover': {
                    color: 'var(--color-text-primary)',
                    textDecorationColor: '#000',
                  },
                }}
              >
                {getTranslation('Txt818', 'HLSkin.AI Terms of Use')}
              </Link>
              {' '}{getTranslation('Txt819', 'and acknowledge the')}{' '}
              {(() => {
                const translationText = getTranslation("Txt820", "Orbo Privacy Policy, % Herbalife Privacy Policy % and Distributor Privacy Policy");
                const parts = translationText.split('%');
                const hasThreeParts = parts.length >= 3;

                 return (
                  <>
                    {hasThreeParts ? (
                      <>
                        {parts[0] && (
                          <Typography
                            component="span"
                            variant="body2"
                            sx={{
                              color: "var(--color-text-primary)",
                              textDecoration: "underline",
                              cursor: "pointer",
                              fontWeight: 700,
                              width: "auto",
                              fontSize: "0.6rem",
                              "&:hover": {
                                color: "var(--color-text-primary)",
                              },
                            }}
                            onClick={handleOrboPrivacyClick}
                          >
                            {parts[0].trim()}{" "}
                          </Typography>
                        )}
                        {parts[1] && (
                          <Typography
                            component="span"
                            variant="body2"
                            sx={{
                              color: "var(--color-text-primary)",
                              textDecoration: "underline",
                              cursor: "pointer",
                              fontWeight: 700,
                              fontSize: "0.6rem",
                              width: "auto",
                              "&:hover": {
                                color: "var(--color-text-primary)",
                              },
                            }}
                            onClick={handleHerbalifePrivacyClick}
                          >
                            {parts[1].trim()}{" "}
                          </Typography>
                        )}
                        {parts[2] && (
                          <Typography
                            component="span"
                            variant="body2"
                            sx={{
                              color: "var(--color-text-primary)",
                              textDecoration: "underline",
                              cursor: "pointer",
                              fontSize: "0.6rem",
                              fontWeight: 700,
                              width: "auto",
                              "&:hover": {
                                color: "var(--color-text-primary)",
                              },
                            }}
                            onClick={handleDistributorPrivacyClick}
                          >
                            {parts[2].trim()}
                          </Typography>
                        )}
                      </>
                    ) : (
                      <>
                        <Typography
                          component="span"
                          variant="body2"
                          sx={{
                            color: "var(--color-text-primary)",
                            textDecoration: "underline",
                            cursor: "pointer",
                            fontWeight: 700,
                            width: "auto",
                            fontSize: "0.6rem",
                          }}
                          onClick={handleOrboPrivacyClick}
                        >
                          {getTranslation("Txt853", "Orbo Privacy Policy")}{" "}
                        </Typography>
                        <Typography component="span" variant="body2" sx={{ fontSize: "0.6rem" }}>
                          {", "}
                        </Typography>
                        <Typography
                          component="span"
                          variant="body2"
                          sx={{
                            color: "var(--color-text-primary)",
                            textDecoration: "underline",
                            cursor: "pointer",
                            fontWeight: 700,
                            width: "auto",
                            fontSize: "0.6rem",
                          }}
                          onClick={handleHerbalifePrivacyClick}
                        >
                          {getTranslation("Txt871", "Herbalife Privacy Policy")}{" "}
                        </Typography>
                        <Typography component="span" variant="body2" sx={{ fontSize: "0.6rem" }}>
                          {"and "}
                        </Typography>
                        <Typography
                          component="span"
                          variant="body2"
                          sx={{
                            color: "var(--color-text-primary)",
                            textDecoration: "underline",
                            cursor: "pointer",
                            fontWeight: 700,
                            width: "auto",
                            fontSize: "0.6rem",
                          }}
                          onClick={handleDistributorPrivacyClick}
                        >
                          {getTranslation("Txt855", "Distributor Privacy Terms")}
                        </Typography>
                      </>
                    )}
                  </>
                );
              })()}
            </Typography>
          </Box>

          {/* Try Now Button */}
          <Button
            onClick={handleTryNowClick}
            disabled={isCreatingSignupSession}
            sx={{
            ...COMMON_MUI_STYLES.primaryCtaButton,
            }}
          >
            {isCreatingSignupSession ? <CircularProgress size={24} color="inherit" /> : getTranslation('Txt5', 'Try Now')}
          </Button>
        </Box>
      </Box>
    </Box>
  );
};

// Desktop View Component
const DesktopView: React.FC<LandingPageViewProps> = ({
  isCreatingLoginSession,
  isCreatingSignupSession,
  selectedLanguage,
  showFullDisclaimer,
  setShowFullDisclaimer,
  handleHLSkinTermsClick,
  handleOrboPrivacyClick,
  handleHerbalifePrivacyClick,
  handleDistributorPrivacyClick,
  handleLanguageChange,
  handleTryNowClick,
  handleLoginClick,
  getTranslation,
}) => {
  return (
    <Box sx={{ width: '100%', height: '100dvh', overflow: 'hidden' }}>
      {/* Login Button */}
      <Box sx={{ position: "absolute", top: 16, left: 16, zIndex: "11" }}>
        <StyledButton
          onClick={handleLoginClick}
          disabled={isCreatingLoginSession}
          sx={{
            lineHeight: "normal",
            fontWeight: 700,
            borderRadius: '24px',
            fontSize: '1.125rem',
            padding: '12px 24px',
            display: 'flex',
            flexDirection: 'row',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '8px',
          }}
        >
          <Box
            component="img"
            src={login_logo}
            alt="Login Logo"
            sx={{
              height: '20px',
              width: 'auto',
              objectFit: 'contain',
              display: 'inline-block',
              verticalAlign: 'middle',
            }}
          />
          <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', marginTop: '4px' }}>
            {isCreatingLoginSession ? <CircularProgress size={20} color="inherit" /> : getTranslation('Txt10', 'Login')}
          </Box>
        </StyledButton>
      </Box>

      {/* Language Selector */}
      <Box sx={{ position: "absolute", top: 16, right: 16, zIndex: "11" }}>
        <LanguageSelector onLanguageChange={handleLanguageChange} />
      </Box>

      {/* Main Content */}
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'row',
          alignItems: 'center',
          justifyContent: 'flex-start',
          width: '100%',
          minHeight: '90%',
          height: '100%',
          position: "absolute"
        }}
      >
        {/* Left Side - Logo */}
        <Box
          sx={{
            width: '50%',
            height: '100%',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'space-around',
            alignItems: 'center',
            padding: '2rem 0',
            gap: '2rem',
            textAlign: 'center',
            flexBasis: '50%',
          }}
        >
          <Box
            component="img"
            src={logo_new}
            alt="Company Logo"
            sx={{ 
              height: "6rem",
              width: "auto",
              maxWidth: "300px",
              objectFit: "contain",
            }}
          />
        </Box>

        {/* Right Side - Image */}
        <Box
          sx={{
            width: '50%',
            height: '80%',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'flex-end',
            alignItems: 'center',
          }}
        >
          <Box
            component="img"
            src={skincareCouple1}
            alt="Home illustration"
            sx={{
              maxWidth: '100%',
              height: '100%',
              objectFit: 'contain',
            }}
          />
        </Box>
      </Box>

      {/* Bottom Section with Background */}
      <Box
        sx={{
          position: "absolute",
          width: "100%",
          backgroundImage: `url(${patch})`,
          backgroundRepeat: "no-repeat",
          backgroundSize: "100% 100%",
          backgroundPosition: "top center",
          bottom: 0,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "flex-end",
          gap: "0.5rem",
          '&::-webkit-scrollbar': {
            width: '6px',
          },
          '&::-webkit-scrollbar-track': {
            background: 'rgba(0, 0, 0, 0.1)',
            borderRadius: '3px',
          },
          '&::-webkit-scrollbar-thumb': {
            background: 'rgba(0, 0, 0, 0.3)',
            borderRadius: '3px',
            '&:hover': {
              background: 'rgba(0, 0, 0, 0.5)',
            },
          },
        }}
      >
        <Box sx={{
          width: "100%",
          height: "100%",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "flex-start",
          gap: "0.5rem",
          padding: "100px 0px 20px",
        }}>
          {/* Title */}
          <Box sx={{ 
            width: "90%",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            textAlign: "center",
          }}>
            <Typography
              variant="body1"
              sx={{
                fontSize: isTamilOrMalayalam(selectedLanguage)
                  ? { md: '2.5rem', lg: '2.5rem' }
                  : { md: '2.6rem', lg: '3rem' },
                lineHeight: 'normal',
                color: 'var(--color-text-primary)',
                fontWeight: 700,
                fontFamily: 'HerbalifeFont',
                textAlign: 'center',
                width: '100%',
              }}
              component="div"
            >
              {getTranslation('Txt3', 'Get a Skin Scan in, Just 60 Seconds')}
            </Typography>
          </Box>

          {/* Disclaimer */}
          <Box sx={{ 
            width: '80%',
            textAlign: 'center',
            mb: 1,
            minHeight: 'fit-content',
          }}>
            <Typography
              sx={{
                fontSize: { sm: '0.7rem', md: '0.9rem' },
                color: 'rgba(0, 0, 0, 1)',
                fontFamily: 'HerbalifeFont',
                fontWeight: 100,
                lineHeight: 1.3,
              }}
            >
              {getTranslation('Txt813', 'This tool is intended solely for cosmetic awareness purposes and is not a substitute for consultation with a qualified healthcare professional.')}{' '}
              {!showFullDisclaimer && (
                <Link
                  href="#"
                  onClick={(e) => {
                    e.preventDefault();
                    setShowFullDisclaimer(true);
                  }}
                  sx={{
                    fontSize: { sm: '0.7rem', md: '0.9rem' },
                    fontWeight: 700,
                    color: 'rgba(0, 0, 0, 1)',
                    textDecoration: 'underline',
                    textDecorationColor: '#000',
                    cursor: 'pointer',
                    '&:hover': {
                      color: 'var(--color-text-primary)',
                      textDecorationColor: '#000',
                    },
                  }}
                >
                  {getTranslation('Txt814', 'Read more')}
                </Link>
              )}
              {showFullDisclaimer && (
                <>
                  {getTranslation('Txt815', 'This tool is designed to help users identify a suitable skincare routine based on general information. It does not provide a professional skin assessment, and the results should not be interpreted as diagnosis, prevention, monitoring, prediction, prognosis, treatment, or alleviation of any medical condition or disease.  If you have any concerns about your skin or overall health, please seek advice from a qualified healthcare professional.')}{' '}
                  <Link
                    href="#"
                    onClick={(e) => {
                      e.preventDefault();
                      setShowFullDisclaimer(false);
                    }}
                    sx={{
                      fontSize: { sm: '0.7rem', md: '0.9rem' },
                      color: 'rgba(0, 0, 0, 1)',
                      fontWeight: 700,
                      textDecoration: 'underline',
                      textDecorationColor: '#000',
                      cursor: 'pointer',
                      '&:hover': {
                        color: 'var(--color-text-primary)',
                        textDecorationColor: '#000',
                      },
                    }}
                  >
                    {getTranslation('Txt816', 'Show less')}
                  </Link>
                </>
              )}
            </Typography>
          </Box>

          {/* Privacy Policy Links */}
          <Box sx={{ 
            width: '90%',
            textAlign: 'center',
            mb: 2,
            minHeight: 'fit-content',
          }}>
            <Typography
              sx={{
                fontSize: { sm: '0.8rem', md: '0.9rem' },
                color: 'var(--color-text-primary)',
                fontFamily: 'HerbalifeFont',
                lineHeight: 1.4,
              }}
            >
              {getTranslation("Txt817", "By clicking on Try Now, I agree to the")}{' '}
              <Link
                href="#"
                onClick={handleHLSkinTermsClick}
                sx={{
                  fontSize: { sm: '0.8rem', md: '0.9rem' },
                  fontWeight: 700,
                  color: 'var(--color-text-primary)',
                  textDecoration: 'underline',
                  textDecorationColor: '#000',
                  cursor: 'pointer',
                  '&:hover': {
                    color: 'var(--color-text-primary)',
                    textDecorationColor: '#000',
                  },
                }}
              >
                {getTranslation('Txt818', 'HLSkin.AI Terms of Use')}
              </Link>
              {' '}{getTranslation('Txt819', 'and acknowledge the')}{' '}
              {(() => {
                const translationText = getTranslation("Txt820", "Orbo Privacy Policy, % Herbalife Privacy Policy % and Distributor Privacy Policy");
                const parts = translationText.split('%');
                const hasThreeParts = parts.length >= 3;

                return (
                  <>
                    {hasThreeParts ? (
                      <>
                        {parts[0] && (
                          <Typography
                            component="span"
                            variant="body2"
                            sx={{
                              color: "var(--color-text-primary)",
                              textDecoration: "underline",
                              cursor: "pointer",
                              fontWeight: 700,
                              width: "auto",
                              fontSize: { md: "0.75rem", lg: "0.8125rem", xl: "0.875rem" },
                              "&:hover": {
                                color: "var(--color-text-primary)",
                              },
                            }}
                            onClick={handleOrboPrivacyClick}
                          >
                            {parts[0].trim()}{" "}
                          </Typography>
                        )}
                        {parts[1] && (
                          <Typography
                            component="span"
                            variant="body2"
                            sx={{
                              color: "var(--color-text-primary)",
                              textDecoration: "underline",
                              cursor: "pointer",
                              fontWeight: 700,
                              fontSize: { md: "0.75rem", lg: "0.8125rem", xl: "0.875rem" },
                              width: "auto",
                              "&:hover": {
                                color: "var(--color-text-primary)",
                              },
                            }}
                            onClick={handleHerbalifePrivacyClick}
                          >
                            {parts[1].trim()}{" "}
                          </Typography>
                        )}
                        {parts[2] && (
                          <Typography
                            component="span"
                            variant="body2"
                            sx={{
                              color: "var(--color-text-primary)",
                              textDecoration: "underline",
                              cursor: "pointer",
                              fontSize: { md: "0.75rem", lg: "0.8125rem", xl: "0.875rem" },
                              fontWeight: 700,
                              width: "auto",
                              "&:hover": {
                                color: "var(--color-text-primary)",
                              },
                            }}
                            onClick={handleDistributorPrivacyClick}
                          >
                            {parts[2].trim()}
                          </Typography>
                        )}
                      </>
                    ) : (
                      <>
                        <Typography
                          component="span"
                          variant="body2"
                          sx={{
                            color: "var(--color-text-primary)",
                            textDecoration: "underline",
                            cursor: "pointer",
                            fontWeight: 700,
                            width: "auto",
                            fontSize: { md: "0.75rem", lg: "0.8125rem", xl: "0.875rem" },
                          }}
                          onClick={handleOrboPrivacyClick}
                        >
                          {getTranslation("Txt853", "Orbo Privacy Policy")}{" "}
                        </Typography>
                        <Typography component="span" variant="body2" sx={{ fontSize: { md: "0.75rem", lg: "0.8125rem", xl: "0.875rem" } }}>
                          {", "}
                        </Typography>
                        <Typography
                          component="span"
                          variant="body2"
                          sx={{
                            color: "var(--color-text-primary)",
                            textDecoration: "underline",
                            cursor: "pointer",
                            fontWeight: 700,
                            width: "auto",
                            fontSize: { md: "0.75rem", lg: "0.8125rem", xl: "0.875rem" },
                          }}
                          onClick={handleHerbalifePrivacyClick}
                        >
                          {getTranslation("Txt871", "Herbalife Privacy Policy")}{" "}
                        </Typography>
                        <Typography component="span" variant="body2" sx={{ fontSize: { md: "0.75rem", lg: "0.8125rem", xl: "0.875rem" } }}>
                          {"and "}
                        </Typography>
                        <Typography
                          component="span"
                          variant="body2"
                          sx={{
                            color: "var(--color-text-primary)",
                            textDecoration: "underline",
                            cursor: "pointer",
                            fontWeight: 700,
                            width: "auto",
                            fontSize: { md: "0.75rem", lg: "0.8125rem", xl: "0.875rem" },
                          }}
                          onClick={handleDistributorPrivacyClick}
                        >
                          {getTranslation("Txt855", "Distributor Privacy Terms")}
                        </Typography>
                      </>
                    )}
                  </>
                );
              })()}
            </Typography>
          </Box>

          {/* Try Now Button */}
          <Button
            onClick={handleTryNowClick}
            disabled={isCreatingSignupSession}
            sx={{
             ...COMMON_MUI_STYLES.primaryCtaButton,
            }}
          >
            {isCreatingSignupSession ? <CircularProgress size={24} color="inherit" /> : getTranslation('Txt5', 'Try Now')}
          </Button>
        </Box>
      </Box>
    </Box>
  );
};

const LandingPage: React.FC = () => {
  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up('md'));
  const {
    setCurrentView,
    setSessionId,
    setSessionError,
    getTranslation,
    distributorId,
    setRole,
    currentLanguage,
    updateLanguage,
    setHideDistributorSignupCta,
  } = useAppContext();
  const [isCreatingLoginSession, setIsCreatingLoginSession] = useState(false);
  const [isCreatingSignupSession, setIsCreatingSignupSession] = useState(false);
  const [selectedLanguage, setSelectedLanguage] = useState(currentLanguage); // Default to context language
  const [showFullDisclaimer, setShowFullDisclaimer] = useState(false);

  
  useEffect(() => {
    if (currentLanguage) {
      setSelectedLanguage(currentLanguage);
    }
  }, [currentLanguage]);

  const handleHLSkinTermsClick = (e: React.MouseEvent) => {
    e.preventDefault();
    sessionStorage.setItem('privacyPolicyPrevView', 'home');
    sessionStorage.setItem('selectedPolicy', 'data-privacy-terms');
    setCurrentView('privacy-policy');
  };

  const handleOrboPrivacyClick = (e: React.MouseEvent) => {
    e.preventDefault();
    sessionStorage.setItem('privacyPolicyPrevView', 'home');
    sessionStorage.setItem('selectedPolicy', 'orbo-privacy');
    setCurrentView('privacy-policy');
  };

  const handleHerbalifePrivacyClick = (e: React.MouseEvent) => {
    e.preventDefault();
    sessionStorage.setItem('privacyPolicyPrevView', 'home');
    sessionStorage.setItem('selectedPolicy', 'herbalife-privacy');
    setCurrentView('privacy-policy');
  };

  const handleDistributorPrivacyClick = (e: React.MouseEvent) => {
    e.preventDefault();
    sessionStorage.setItem('privacyPolicyPrevView', 'home');
    sessionStorage.setItem('selectedPolicy', 'distributor-privacy-terms');
    setCurrentView('privacy-policy');
  };

  const handleLanguageChange = (sessionLanguage: string) => {
    setSelectedLanguage(sessionLanguage);
    updateLanguage(sessionLanguage); // Use context helper
  };

  const handleTryNowClick = async () => {
    setIsCreatingSignupSession(true);
    try {
      await createSession(
        setSessionId,
        setSessionError,
        selectedLanguage,
        undefined,
        setRole,
        undefined,
        distributorId || undefined,
      );
      setHideDistributorSignupCta(false);
      if (distributorId) {
        setCurrentView('signup');
      } else {
        setCurrentView('contact');
      }
    } catch (error) {
      console.error('Failed to create session:', error);
      try {
        await createSession(
          setSessionId,
          setSessionError,
          selectedLanguage,
          undefined,
          setRole,
          undefined,
        );
      } catch (retryError) {
        console.error(
          'Retry createSession without distributorId failed:',
          retryError,
        );
      }
      setHideDistributorSignupCta(true);
      setCurrentView('contact');
    } finally {
      setIsCreatingSignupSession(false);
    }
  };

  const handleLoginClick = async () => {
    setIsCreatingLoginSession(true);
    try {
      await createSession(setSessionId, setSessionError, selectedLanguage, undefined, setRole, undefined);
      setCurrentView('contact');
    } catch (error) {
      console.error('Failed to create session:', error);
      setCurrentView('contact');
    } finally {
      setIsCreatingLoginSession(false);
    }
  };

  const viewProps: LandingPageViewProps = {
    isCreatingLoginSession,
    isCreatingSignupSession,
    selectedLanguage,
    showFullDisclaimer,
    setShowFullDisclaimer,
    handleHLSkinTermsClick,
    handleOrboPrivacyClick,
    handleHerbalifePrivacyClick,
    handleDistributorPrivacyClick,
    handleLanguageChange,
    handleTryNowClick,
    handleLoginClick,
    getTranslation,
  };

  return isDesktop ? <DesktopView {...viewProps} /> : <MobileView {...viewProps} />;
};

export default LandingPage;
